import React, { useState, useCallback, useEffect, useRef } from 'react'
import { apiFetch, recoverApiAuthorization } from '../api'

interface BankImportPerformance {
	averageMsPerTransaction: number,
	transactionCount: number,
	totalMs: number,
	sampleCount: number,
	updatedAt: string,
}

interface BancFileUploaderProps {
	onImported?: (inserted: number) => void,
}

const parseUploadResponse = async (response: Response) => {
	const contentType = response.headers.get('content-type') || ''
	const payload = contentType.includes('application/json')
		? await response.json()
		: { message: await response.text() }
	if (!response.ok) {
		throw new Error(payload?.error || payload?.message || `Bank import failed with status ${response.status}`)
	}
	return payload
}

const BancFileUploader = ({ onImported }: BancFileUploaderProps) => {
	const [inserted, setInserted] = useState<number | undefined>(undefined)
	const [isDragging, setIsDragging] = useState(false)
	const [isUploading, setIsUploading] = useState(false)
	const [importPerformance, setImportPerformance] = useState<BankImportPerformance | undefined>()
	const [estimatedMs, setEstimatedMs] = useState<number | undefined>()
	const [uploadProgress, setUploadProgress] = useState(0)
	const progressStartedAt = useRef<number | undefined>()

	interface DragEvent extends React.DragEvent<HTMLDivElement> {
		dataTransfer: DataTransfer
	}
	interface ChangeEvent extends React.ChangeEvent<HTMLInputElement> {
		target: HTMLInputElement & EventTarget
	}

	useEffect(() => {
		apiFetch('/bank/import-performance')
			.then(response => response.json())
			.then(data => setImportPerformance(data.performance))
			.catch(error => {
				recoverApiAuthorization(error)
				console.error('Error fetching bank import performance:', error)
			})
	}, [])

	useEffect(() => {
		if (!isUploading || estimatedMs === undefined) return
		progressStartedAt.current = Date.now()
		setUploadProgress(0)
		const interval = window.setInterval(() => {
			const elapsed = Date.now() - (progressStartedAt.current || Date.now())
			setUploadProgress(Math.min(96, Math.round((elapsed / estimatedMs) * 100)))
		}, 200)
		return () => window.clearInterval(interval)
	}, [estimatedMs, isUploading])

	const filesToFormData = (files: FileList): FormData | undefined => {
		const formData = new FormData()
		for (let i = 0; i < files.length; i++) {
			const file = files[i]
			if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' && file.type !== 'application/vnd.ms-excel') {
				console.error('Invalid file type. Please upload an Excel file.')
				return undefined
			}
			formData.append('files', file)
		}
		return formData
	}

	const onDrop = useCallback((event: DragEvent | ChangeEvent) => {
		event.preventDefault()
		setInserted(undefined)
		setIsDragging(false)
		setUploadProgress(0)
		setEstimatedMs(undefined)
		const files = 'dataTransfer' in event ? event.dataTransfer.files : event.target.files
		if (!files) return undefined
		const upload = (nextEstimatedMs?: number) => {
			const uploadFormData = filesToFormData(files)
			if (!uploadFormData) return undefined
			setEstimatedMs(nextEstimatedMs)
			setIsUploading(true)
			apiFetch('/bank', {
				method: 'POST',
				body: uploadFormData,
			})
			.then(parseUploadResponse)
			.then(data => {
				setUploadProgress(100)
				setIsUploading(false)
				setInserted(data.inserted)
				if (Number(data.inserted) > 0) {
					onImported?.(Number(data.inserted))
				}
				if (data.performance) {
					setImportPerformance(data.performance)
				}
			})
			.catch(error => {
				recoverApiAuthorization(error)
				setIsUploading(false)
				console.error('Error uploading bank file:', error)
			})
		}
		if (!importPerformance) {
			upload()
			return undefined
		}
		const estimateFormData = filesToFormData(files)
		if (!estimateFormData) return undefined
		const optimisticEstimatedMs = Math.max(importPerformance.averageMsPerTransaction, 1) * Math.max(files.length, 1)
		setEstimatedMs(optimisticEstimatedMs)
		setIsUploading(true)
		apiFetch('/bank/import-estimate', {
			method: 'POST',
			body: estimateFormData,
		})
			.then(parseUploadResponse)
			.then(data => {
				upload(data.estimatedMs || optimisticEstimatedMs)
			})
			.catch(error => {
				recoverApiAuthorization(error)
				console.error('Error estimating bank import:', error)
				upload()
			})
	}, [importPerformance, onImported])

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault()
		setIsDragging(true)
	}

	const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault()
		setIsDragging(false)
	}

	return (
		<div
			onDrop={onDrop}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			className={`file-uploader${isDragging ? ' dragging' : ''}${isUploading && estimatedMs !== undefined ? ' progress' : ''}`}
		>
			{isUploading && estimatedMs !== undefined &&
				<div className="bank-import-progress-fill" style={{ width: `${uploadProgress}%` }} aria-hidden="true" />
			}
			<input type="file" multiple onChange={onDrop} style={{ display: 'none' }} id="fileInput" />
			<label htmlFor="fileInput" style={{ cursor: 'pointer' }}>
				{inserted === undefined && !isDragging && !isUploading &&
					<span>Echar aquí el Excel del banco, o tocar aquí para seleccionarlo</span>
				}
				{inserted !== undefined && !isDragging && !isUploading &&
					<span><strong>{inserted}</strong> movimientos importados</span>
				}
				{isDragging && !isUploading &&
					<span>Suelta el archivo aquí</span>
				}
				{isUploading && estimatedMs === undefined &&
					<div className="uploading-animation">
						<div className="spinner"></div>
					</div>
				}
				{isUploading && estimatedMs !== undefined &&
					<div className="bank-import-progress" aria-label="Importando movimientos">
						<span>{uploadProgress}%</span>
					</div>
				}
			</label>
		</div>
	)
}

export default BancFileUploader
